/**
 * Translation table: Polish XLSX header -> DB field path.
 *
 * Keys are Polish header labels from the Nosniki sheet with required-suffix
 * stripped (i.e. key is 'Numer Grupy RW', not 'Numer Grupy RW *').
 *
 * Used by the bulk-import parser (CU-86c8zc59g) to map columns to DB
 * fields. DB column names stay English (D-1 decision).
 *
 * Covers REQ-2.21.
 *
 * Source of truth for column names and order: Marta's annotated XLSX in
 * ClickUp task CU-86c9m0j2c. 48 columns, exact order as per Marta's final
 * formatka (Nosniki sheet header row).
 *
 * DOOH is completely out of scope for this template. Only OOH columns are
 * included. Transit will get its own formatka in a later phase.
 */

export interface XlsxTranslationEntry {
  table: string;
  column: string;
}

/**
 * Canonical ordered list of 48 headers for the OOH inventory workbook
 * (`data/szablon_OOH.xlsx`). This is the sole source of truth — column
 * names, required flags, and order come from Marta's final formatka.
 */
export const NOSNIKI_HEADERS = [
  // ── 1. Location ───────────────────────────────────────────────
  'Województwo',
  'Powiat',
  'Aglomeracja', // NOTE: Aglomeracja BEFORE Miejscowość (Marta's order)
  'Miejscowość',
  'Adres',
  'Nr drogi (dla konstrukcji)',
  'Współrzędne - szerokość',
  'Współrzędne - długość',
  // ── 2. Identification ─────────────────────────────────────────
  'Numer Grupy RW',
  'Link do karty video dla WF',
  'Numer dostawcy',
  'Dostawca',
  'Typ nośnika',
  'Format',
  // ── 3. Physical params ────────────────────────────────────────
  'Szerokość [m]',
  'Wysokość [m]',
  'Wymiary niestandardowe',
  'Materiał',
  'Oświetlenie',
  // ── 4. Audience ───────────────────────────────────────────────
  'Szacowana liczba odbiorców',
  // ── 5. New: Multiformat ───────────────────────────────────────
  'Multiformat',
  // ── 6. Common metadata ────────────────────────────────────────
  'Osoba do kontaktu przy WF i muralach',
  'Opis lokalizacji',
  'Uwagi',
  // ── 7. Mural variant flags ────────────────────────────────────
  '[Mural] Element 3D',
  '[Mural] Taśma LED',
  '[Mural] Neon LED',
  '[Mural] Fluo UV',
  // ── 8. Costs / client prices ──────────────────────────────────
  'Czynsz',
  'Koszt ekspozycji',
  'Cena katalogowa',
  'Cena po rabacie',
  'Cena last minute',
  'RW na start',
  'Koszt produkcji',
  'Produkcja',
  'Koszt montażu',
  'Montaż',
  'Koszt demontażu',
  'Demontaż',
  'Koszty dodatkowe',
  'Opłaty dodatkowe',
  '[Mural] Produkcja eco muralu',
  // ── 9. Tags ───────────────────────────────────────────────────
  'Tag 1',
  'Tag 2',
  'Tag 3',
  'Tag 4',
  'Tag 5',
  // ── 10. Photos ─────────────────────────────────────────────────
  'Zdjęcia',
] as const;

export type XlsxTemplateTranslation = Record<
  (typeof NOSNIKI_HEADERS)[number],
  XlsxTranslationEntry
>;

/**
 * Maps Polish header (required-suffix stripped) to { table, column }.
 *
 * Tag 1..5 map to a placeholder table/column ('media_unit_tags.tag') until
 * the proper tag system ticket lands.
 *
 * Multiformat maps to media_units.multiformat — DB migration deferred.
 */
export const XLSX_TEMPLATE_TRANSLATION: XlsxTemplateTranslation = {
  // ── 1. Location ───────────────────────────────────────────────
  Województwo: { table: 'locations', column: 'region' },
  Powiat: { table: 'locations', column: 'county' },
  Aglomeracja: { table: 'locations', column: 'agglomeration' },
  Miejscowość: { table: 'media_units', column: 'city' },
  Adres: { table: 'locations', column: 'address' },
  'Nr drogi (dla konstrukcji)': { table: 'media_unit_variants', column: 'road_number' },
  'Współrzędne - szerokość': { table: 'locations', column: 'latitude' },
  'Współrzędne - długość': { table: 'locations', column: 'longitude' },
  // ── 2. Identification ─────────────────────────────────────────
  'Numer Grupy RW': { table: 'media_units', column: 'grw_id' },
  'Link do karty video dla WF': { table: 'media_unit_variants', column: 'videocard_link' },
  'Numer dostawcy': { table: 'media_units', column: 'supplier_number' },
  Dostawca: { table: 'suppliers', column: 'name' },
  'Typ nośnika': { table: 'media_unit_variants', column: 'media_type' },
  Format: { table: 'media_formats', column: 'code' },
  // ── 3. Physical params ────────────────────────────────────────
  'Szerokość [m]': { table: 'media_units', column: 'width_m' },
  'Wysokość [m]': { table: 'media_units', column: 'height_m' },
  'Wymiary niestandardowe': { table: 'media_units', column: 'dimension_notes' },
  Materiał: { table: 'media_units', column: 'material' },
  Oświetlenie: { table: 'media_units', column: 'lighting' },
  // ── 4. Audience ───────────────────────────────────────────────
  'Szacowana liczba odbiorców': { table: 'media_units', column: 'audience_estimate' },
  // ── 5. Multiformat ────────────────────────────────────────────
  // DB migration deferred — placeholder column name.
  Multiformat: { table: 'media_units', column: 'multiformat' },
  // ── 6. Common metadata ────────────────────────────────────────
  'Osoba do kontaktu przy WF i muralach': { table: 'media_units', column: 'wf_contact_person' },
  'Opis lokalizacji': { table: 'media_units', column: 'location_description' },
  Uwagi: { table: 'media_units', column: 'notes' },
  // ── 7. Mural variant flags ────────────────────────────────────
  '[Mural] Element 3D': { table: 'media_unit_variants', column: 'mural_element_3d' },
  '[Mural] Taśma LED': { table: 'media_unit_variants', column: 'mural_led_tape' },
  '[Mural] Neon LED': { table: 'media_unit_variants', column: 'mural_neon_led' },
  '[Mural] Fluo UV': { table: 'media_unit_variants', column: 'mural_fluo_uv' },
  // ── 8. Costs / client prices ──────────────────────────────────
  Czynsz: { table: 'media_unit_costs', column: 'rent' },
  'Koszt ekspozycji': { table: 'media_unit_costs', column: 'exposure_cost' },
  'Cena katalogowa': { table: 'media_unit_costs', column: 'catalog_price' },
  'Cena po rabacie': { table: 'media_unit_costs', column: 'discounted_price' },
  'Cena last minute': { table: 'media_unit_costs', column: 'last_minute_price' },
  'RW na start': { table: 'media_unit_costs', column: 'starter_price' },
  'Koszt produkcji': { table: 'media_unit_costs', column: 'production_cost' },
  Produkcja: { table: 'media_unit_costs', column: 'client_production' },
  'Koszt montażu': { table: 'media_unit_costs', column: 'installation_cost' },
  Montaż: { table: 'media_unit_costs', column: 'client_installation' },
  'Koszt demontażu': { table: 'media_unit_costs', column: 'dismantling_cost' },
  Demontaż: { table: 'media_unit_costs', column: 'client_dismantling' },
  'Koszty dodatkowe': { table: 'media_unit_costs', column: 'additional_costs' },
  'Opłaty dodatkowe': { table: 'media_unit_costs', column: 'client_additional_fees' },
  '[Mural] Produkcja eco muralu': { table: 'media_unit_costs', column: 'mural_eco_production' },
  // ── 9. Tags ───────────────────────────────────────────────────
  // Placeholder mapping; all 5 columns merge into one tag list server-side.
  'Tag 1': { table: 'media_unit_tags', column: 'tag' },
  'Tag 2': { table: 'media_unit_tags', column: 'tag' },
  'Tag 3': { table: 'media_unit_tags', column: 'tag' },
  'Tag 4': { table: 'media_unit_tags', column: 'tag' },
  'Tag 5': { table: 'media_unit_tags', column: 'tag' },
  // ── 10. Photos ─────────────────────────────────────────────────
  // Transient field — not persisted to media_units. The worker splits this
  // comma-separated cell into ParsedRow.photoUrls and fetches/ingests each
  // URL into media_photos via PhotoIngestService after the row upsert.
  Zdjęcia: { table: 'media_units', column: 'photo_urls' },
};
